import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';

interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api/v1';

const api: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor de request: anexa o access token em toda requisição
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Toda listagem da API é paginada (Page<T>), 10 por página no backend. As telas de
    // listagem pedem `page` e ficam com esse tamanho (ver use/usePagina); quem não pede
    // (selects, rotinas, sino) quer a lista inteira — leva 100, o max-page-size do backend.
    // ponytail: teto de 100 itens fora das telas paginadas.
    if (config.method === 'get' && config.params?.size === undefined && config.params?.page === undefined) {
      config.params = { ...config.params, size: 100 };
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Controle de fila para requisições que chegam durante o refresh
let isRefreshing = false;

interface QueueItem {
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}

let failedQueue: QueueItem[] = [];

const processQueue = (error: unknown, token: string | null = null): void => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token as string);
    }
  });
  failedQueue = [];
};

// Interceptor de response: renova o token automaticamente ao receber 401
api.interceptors.response.use(
  // Page<T> do Spring Data vira o array de sempre para quem não pediu `page`: só as
  // telas paginadas recebem a página inteira (com totalPages). Os dois campos no
  // guard são de propósito — ChatMessage também tem um `content`, mas é string.
  (response) => {
    const d = response.data;
    const pediuPagina = response.config?.params?.page !== undefined;
    if (!pediuPagina && d && Array.isArray(d.content) && typeof d.totalElements === 'number') {
      response.data = d.content;
    }
    return response;
  },

  async (error) => {
    const originalRequest = error.config as RetryableRequestConfig;

    // 402: o backend cortou o acesso por falta de assinatura ativa
    // (SubscriptionGuardFilter). Ponto único de tratamento — qualquer tela que
    // esbarre no gate cai na de planos, sem cada hook precisar saber disso.
    if (error.response?.status === 402 && window.location.pathname !== '/subscription') {
      window.location.href = '/subscription';
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      // Enfileira requisições que chegam enquanto o refresh está em andamento
      if (isRefreshing) {
        return new Promise<string>((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        isRefreshing = false;
        localStorage.clear();
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        const response = await axios.post(`${BASE_URL}/auth/refresh`, { refreshToken });
        const { accessToken } = response.data as { accessToken: string };

        localStorage.setItem('authToken', accessToken);
        api.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
        processQueue(null, accessToken);
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        localStorage.clear();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;
